import React, { useEffect, useState, lazy, Suspense,useMemo } from "react";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  logoutUserAsync,
  ToggleSessionAlert,
  UpdateSessionAlert,
  setOfflineStatus, 
  setServerDown
} from "../redux/authSlice";
import Loader from "../components/Loader";
// import MasterAdminRoutes from "./MasterAdminRoutes";
import Swal from "sweetalert2";
import ErrorBoundary from "../components/ErrorBoundary";
const AuthRoutes = lazy(() => import("./AuthRoutes"));
const AdminRoutes = lazy(() => import("./AdminRoutes"));
const MasterAdminRoutes = lazy(() => import("./MasterAdminRoutes"));
const BusinessPortalRoutes = lazy(() => import("./BusinessPortalRoutes"));
const PublicRoutes = lazy(() => import("./PublicRoutes"));
const UserManagementPortalRoutes = lazy(() => import("./UserManagementPortalRoutes"));

function AppContent() {
  const dispatch = useDispatch();
dispatch(setOfflineStatus(!navigator.onLine));

// Listen for changes
window.addEventListener("offline", () => {
  dispatch(setOfflineStatus(true));
});

window.addEventListener("online", () => {
  dispatch(setOfflineStatus(false));
  dispatch(setServerDown(false))
});
  let { userType, showSessiosAlert, userDetails, loading, showUnauthorizedModal } = useSelector(
    (state) => state.auth
  );
  const location = useLocation();
  const isUMPPath = location.pathname.startsWith("/userportal");

  const isBusinessPortalPath = window.location.pathname.startsWith(
    "/businessSubscriptionPortal"
  );

  const isPublicPath =
    window.location.pathname.startsWith("/privacy-policy") ||
    window.location.pathname.startsWith("/contact-us") ||
    window.location.pathname.startsWith("/delete_account") ||
    window.location.pathname.startsWith("/verifyToLogin") ||
    window.location.pathname.startsWith("/ForgetPasswordUser");

  // Offline / online listeners
  useEffect(() => {
    dispatch(setOfflineStatus(!navigator.onLine));
    const handleOffline = () => dispatch(setOfflineStatus(true));
    const handleOnline = () => {
      dispatch(setOfflineStatus(false));
      dispatch(setServerDown(false));
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [dispatch]);

  // Session-expired SweetAlert — skip for business portal, UMP, and public paths
  useEffect(() => {
    if (showSessiosAlert && userDetails && !isBusinessPortalPath && !isPublicPath && !isUMPPath) {
      Swal.fire({
        text: "Your session has expired due to inactivity.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Logout",
        cancelButtonText: "Extend Session",
        confirmButtonColor: "#dc3545",
        cancelButtonColor: "#28a745",
      }).then((result) => {
        if (result.isConfirmed) {
          const loggedInUser = JSON.parse(
            sessionStorage.getItem("userLoggedDetails")
          );
          if (!loggedInUser) return;
          const data = {
            id: loggedInUser?.id,
            requestType: '0',
            isSwitch: false,
            accType: loggedInUser?.accountType
          };
          dispatch(logoutUserAsync(data));
          sessionStorage.clear();
          dispatch(ToggleSessionAlert(false));
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          dispatch(ToggleSessionAlert(false));
        }
      });
    }
  }, [showSessiosAlert, dispatch, userDetails, isBusinessPortalPath, isPublicPath, isUMPPath]);

  const routes = useMemo(() => {
    // User Management Portal — checked first to prevent businessAuth state from hijacking
    if (isUMPPath) {
      return <UserManagementPortalRoutes />;
    }

    // Public pages — accessible to everyone
    if (isPublicPath) {
      return <PublicRoutes />;
    }

    // Business portal — path-gated only; loginSource check inside BusinessProtectedRoute
    if (isBusinessPortalPath) {
      return <BusinessPortalRoutes />;
    }

    switch (userType) {
      case "UNREGISTERED":
        return <AuthRoutes />;
      case "admin":
        return <AdminRoutes />;
      case "masteradmin":
        return <MasterAdminRoutes />;
      default:
        return <AuthRoutes />;
    }
  }, [userType, isUMPPath, isPublicPath, isBusinessPortalPath]);

  const modalStyles = {
    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(0, 0, 0, 0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
    },
    modalContent: {
      background: "white",
      padding: "20px",
      borderRadius: "8px",
      textAlign: "center",
      boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
      maxWidth: "400px",
      width: "100%",
    },
    modalButtons: {
      marginTop: "20px",
    },
    button: {
      margin: "0 10px",
      padding: "10px 20px",
      backgroundColor: "#007bff",
      color: "white",
      border: "none",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "16px",
    },
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<Loader />}>
        {routes}
      </Suspense>
    </ErrorBoundary>
  );
}

export default function Routes() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}